import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { Card, StatCard, Badge, Muted } from "../ui";

interface DashboardData {
  sales_today: {
    transactions: number;
    total_sales: number;
    cash_sales: number;
    card_sales: number;
    voids: number;
  };
  recent_sales: Array<{
    id: string;
    actual_amount_paid: number;
    payment_method: string;
    completed_at: string;
    cashier_name: string;
    item_count: number;
  }>;
  low_stock: Array<{
    name: string;
    oz_on_hand: number;
    cases_on_hand: number;
    min_oz: number;
  }>;
  active_taps: Array<{
    tap_number: number;
    brand: string;
    product_name: string;
    percent_remaining: number;
  }>;
  open_tasks: Array<{
    title: string;
    priority: string;
    created_at: string;
  }>;
  pending_pos: Array<{
    id: string;
    vendor_name: string;
    status: string;
    total_amount: number;
    expected_delivery_date: string | null;
  }>;
  today_schedule: Array<{
    name: string;
    entry_type: string;
    cutoff_time: string | null;
  }>;
  drawer_session: {
    id: string;
    opening_balance: number;
    cash_sales_total: number;
    opened_at: string;
  } | null;
}

const PRIORITY_COLORS: Record<string, string> = {
  urgent: "var(--rbw-danger)",
  high: "var(--rbw-gold-dark)",
  normal: "var(--rbw-navy)",
  low: "var(--rbw-gray-dark)",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(console.error);
  }, []);

  if (!data) {
    return (
      <div className="flex items-center justify-center h-full">
        <Muted>Loading dashboard...</Muted>
      </div>
    );
  }

  const sales = data.sales_today;
  const now = new Date();
  const greeting = now.getHours() < 12 ? "Good morning" : now.getHours() < 17 ? "Good afternoon" : "Good evening";

  return (
    <div className="max-w-7xl mx-auto px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight" style={{ color: "var(--rbw-navy)" }}>
          {greeting}, {user?.first_name}
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
          {now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
        </p>
      </div>

      {/* Today's sales stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Today's Sales"
          value={`$${Number(sales.total_sales).toFixed(2)}`}
          detail={`${sales.transactions} transaction${Number(sales.transactions) !== 1 ? "s" : ""}`}
          variant="gold"
        />
        <StatCard
          label="Cash / Debit"
          value={`$${Number(sales.cash_sales).toFixed(2)}`}
          variant="green"
        />
        <StatCard
          label="Credit Card"
          value={`$${Number(sales.card_sales).toFixed(2)}`}
          variant="blue"
        />
        <StatCard
          label="Drawer"
          value={data.drawer_session ? "Open" : "Closed"}
          detail={data.drawer_session ? `Since ${new Date(data.drawer_session.opened_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "No active session"}
          variant={data.drawer_session ? "green" : "default"}
        />
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left column */}
        <div className="col-span-2 space-y-6">
          {/* Today's vendor schedule */}
          {data.today_schedule.length > 0 && (
            <Card padding="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold" style={{ color: "var(--rbw-navy)" }}>Today's Schedule</h3>
                <Link to="/purchase-orders" className="text-xs font-medium" style={{ color: "var(--rbw-blue-dark)" }}>View Orders &rarr;</Link>
              </div>
              <div className="flex flex-wrap gap-2">
                {data.today_schedule.map((entry, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg"
                    style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
                  >
                    <Badge variant={entry.entry_type === "order_due" ? "gold" : "green"}>
                      {entry.entry_type === "order_due" ? "Order" : "Delivery"}
                    </Badge>
                    <span className="text-sm font-medium" style={{ color: "var(--rbw-navy)" }}>{entry.name}</span>
                    {entry.cutoff_time && (
                      <span className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>by {entry.cutoff_time}</span>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Recent sales */}
          <Card padding="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold" style={{ color: "var(--rbw-navy)" }}>Recent Sales</h3>
              <Link to="/reports" className="text-xs font-medium" style={{ color: "var(--rbw-blue-dark)" }}>All Reports &rarr;</Link>
            </div>
            {data.recent_sales.length === 0 ? (
              <div className="text-center py-6"><Muted>No sales today yet</Muted></div>
            ) : (
              <div className="space-y-2">
                {data.recent_sales.map((sale) => (
                  <div
                    key={sale.id}
                    className="flex items-center justify-between px-3 py-2.5 rounded-lg"
                    style={{ background: "var(--rbw-bg)" }}
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant={sale.payment_method === "cash" ? "gold" : sale.payment_method === "credit" ? "blue" : "green"}>
                        {sale.payment_method}
                      </Badge>
                      <div>
                        <span className="text-sm font-medium" style={{ color: "var(--rbw-navy)" }}>
                          {sale.item_count} item{Number(sale.item_count) !== 1 ? "s" : ""}
                        </span>
                        <span className="text-xs ml-2"><Muted>{sale.cashier_name}</Muted></span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold" style={{ color: "var(--rbw-navy)" }}>
                        ${Number(sale.actual_amount_paid).toFixed(2)}
                      </div>
                      <div className="text-xs">
                        <Muted>{new Date(sale.completed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</Muted>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Pending POs */}
          {data.pending_pos.length > 0 && (
            <Card padding="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold" style={{ color: "var(--rbw-navy)" }}>Pending Orders</h3>
                <Link to="/purchase-orders" className="text-xs font-medium" style={{ color: "var(--rbw-blue-dark)" }}>View All &rarr;</Link>
              </div>
              <div className="space-y-2">
                {data.pending_pos.map((po) => (
                  <Link
                    key={po.id}
                    to={`/purchase-orders`}
                    className="flex items-center justify-between px-3 py-2.5 rounded-lg transition-all"
                    style={{ background: "var(--rbw-bg)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "var(--rbw-gray-light)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "var(--rbw-bg)")}
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant={po.status === "draft" ? "default" : "blue"}>{po.status}</Badge>
                      <span className="text-sm font-medium" style={{ color: "var(--rbw-navy)" }}>{po.vendor_name}</span>
                    </div>
                    <div className="text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>
                      ${Number(po.total_amount).toFixed(2)}
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Quick actions */}
          <Card padding="p-5">
            <h3 className="font-bold mb-3" style={{ color: "var(--rbw-navy)" }}>Quick Actions</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { to: "/register", label: "Register", bg: "var(--rbw-gold)", color: "var(--rbw-navy)" },
                { to: "/purchase-orders", label: "New Order", bg: "var(--rbw-navy)", color: "#fff" },
                { to: "/inventory", label: "Inventory", bg: "var(--rbw-bg)", color: "var(--rbw-navy)" },
                { to: "/needs-done", label: "Tasks", bg: "var(--rbw-bg)", color: "var(--rbw-navy)" },
              ].map((action) => (
                <Link
                  key={action.to}
                  to={action.to}
                  className="px-3 py-3 rounded-xl text-center text-sm font-semibold transition-all active:scale-[0.97]"
                  style={{ background: action.bg, color: action.color, border: "1px solid var(--rbw-border)" }}
                >
                  {action.label}
                </Link>
              ))}
            </div>
          </Card>

          {/* Low stock alerts */}
          <Card padding="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold" style={{ color: "var(--rbw-navy)" }}>Low Stock</h3>
              <Link to="/inventory" className="text-xs font-medium" style={{ color: "var(--rbw-blue-dark)" }}>View All &rarr;</Link>
            </div>
            {data.low_stock.length === 0 ? (
              <div className="text-center py-4">
                <div className="text-sm" style={{ color: "var(--rbw-success)" }}>All stocked up</div>
              </div>
            ) : (
              <div className="space-y-2">
                {data.low_stock.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5">
                    <span className="text-sm truncate pr-2" style={{ color: "var(--rbw-navy)" }}>{item.name}</span>
                    <span className="text-xs font-bold flex-shrink-0" style={{ color: "var(--rbw-danger)" }}>
                      {Number(item.cases_on_hand).toFixed(1)} cases
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Kegs on tap */}
          {data.active_taps.length > 0 && (
            <Card padding="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold" style={{ color: "var(--rbw-navy)" }}>On Tap</h3>
                <Link to="/kegs" className="text-xs font-medium" style={{ color: "var(--rbw-blue-dark)" }}>Manage &rarr;</Link>
              </div>
              <div className="space-y-2">
                {data.active_taps.map((tap) => {
                  const pct = Number(tap.percent_remaining);
                  return (
                    <div key={tap.tap_number} className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                        style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
                      >
                        {tap.tap_number}
                      </div>
                      <span className="text-sm truncate flex-1" style={{ color: "var(--rbw-navy)" }}>
                        {tap.brand || tap.product_name}
                      </span>
                      <div className="w-12 h-1.5 rounded-full flex-shrink-0" style={{ background: "var(--rbw-gray-light)" }}>
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.max(5, pct)}%`,
                            background: pct > 50 ? "var(--rbw-success)" : pct > 25 ? "var(--rbw-gold)" : "var(--rbw-danger)",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Open tasks */}
          {data.open_tasks.length > 0 && (
            <Card padding="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold" style={{ color: "var(--rbw-navy)" }}>Needs Done</h3>
                <Link to="/needs-done" className="text-xs font-medium" style={{ color: "var(--rbw-blue-dark)" }}>View All &rarr;</Link>
              </div>
              <div className="space-y-2">
                {data.open_tasks.map((task, i) => (
                  <div key={i} className="flex items-center gap-2 py-1">
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: PRIORITY_COLORS[task.priority] || "var(--rbw-gray)" }}
                    />
                    <span className="text-sm truncate" style={{ color: "var(--rbw-navy)" }}>{task.title}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
